import { LocationQueryValue } from 'vue-router'
import { AnyObject } from './GenericInterfaces'
import { ProductPropertyStatus } from './Product'
import { PropertyOptions, PropertyReturn } from './Property'

export interface Culture {
  language: Language
  country: Country
  mainLanguageCulture?: boolean // uses culture for the language flags
}

export type Country = 'se' | 'dk' | 'no' | 'fi' | 'de' | 'fr' | 'uk' | 'us' | 'es' | 'it' | 'nl' | 'at' | 'ie' | 'ch'
export type Language = 'sv' | 'da' | 'nb' | 'fi' | 'de' | 'fr' | 'enUK' | 'enUS' | 'es' | 'it' | 'nlNL'

export type CultureSpecificity = 'languageSpecific' | 'countrySpecific'

export const ImportTypes = ['translationsImport', 'productsImport'] as const
export type ImportType = (typeof ImportTypes)[number]

export const ProgressTypes = [...ImportTypes, 'eanLabelExport', 'web-sync', 'productsBulkEdit', 'singularProduct', 'productsExport'] as const
export type ProgressType = (typeof ProgressTypes)[number]

export interface Progress<T = any> {
  id?: string
  progressType: ProgressType
  currentAction?: string
  percentage?: number
  progressLog?: Set<string> // currentActions with progress bars

  // used by ean labels export
  processed?: number // processed # of items
  total?: number // total # of items

  // used by e-commerce sync
  successful?: boolean
  data?: T

  finished?: boolean
  endDate?: Date

  children?: ChildProgress<T>[]
}

export type ChildProgress<T> = Omit<Progress<T>, 'progressType' | 'children'>

export interface ProgressReport extends Progress {}

export interface Sorting {
  field: string
  order: SortOrder
  secondaryField?: string
  secondaryOrder?: SortOrder
}

export type SortOrder = 'asc' | 'desc'

export interface BasicOption<T = string | undefined> {
  value: T
  displayValue: string
  disabled?: boolean
}

export interface Option<T = string | undefined> extends BasicOption<T> {
  experimental?: boolean
  exclusiveToCountries?: Country[]
  exclusiveToLanguages?: Language[]
  parent?: string
  icon?: string | string[]
  staged?: boolean
}

export interface HierarchicalOption<T = string | undefined> extends BasicOption<T> {
  parentId?: string
  staged?: boolean

  level?: number
}

export const CountryFlagSymbols: Record<Country, string> = {
  se: '🇸🇪',
  dk: '🇩🇰',
  no: '🇳🇴',
  fi: '🇫🇮',
  de: '🇩🇪',
  fr: '🇫🇷',
  uk: '🇬🇧',
  us: '🇺🇸',
  es: '🇪🇸',
  it: '🇮🇹',
  at: '🇦🇹',
  ie: '🇮🇪',
  nl: '🇳🇱',
  ch: '🇨🇭',
}

export interface CultureOverrides {
  dataLanguage?: Language | null
  dataCountry?: Country | null
  translationLanguage?: Language | null
  includeCultureExclusiveData?: boolean
  /** @deprecated */
  includeAllCultures?: boolean
  includedCountries?: Country[]
  includeProductsMissingAvailableCountries?: boolean
}

export interface BasicResponse {
  msg: string
  status: number
}

export type SearchMode = 'split-words' | 'regex' | 'normal'

export type SimpleValueType = string | number | null | undefined

export type PimLocationQuery = Record<string, LocationQueryValue | LocationQueryValue[] | number>

export type LocalEnvironment = 'production' | 'development' | 'stage' | 'beta'

export enum Direction {
  forwards = 1,
  backwards = -1,
}

export const isTouchDevice = !!('ontouchstart' in window) || (!!('onmsgesturechange' in window) && !!window.navigator.maxTouchPoints)

export interface LinkGroup {
  header: string
  links: Array<{
    label: string
    url: string
  }>
}

export type CopiedData = { text: string | undefined; propertyReturn?: PropertyReturn }
